import { useEffect } from 'react'
import { gql } from '@apollo/client'
import type { ErrorLike } from '@apollo/client'
import { CombinedGraphQLErrors } from '@apollo/client/errors'
import { useLazyQuery } from '@apollo/client/react'
import { getContentfulClient, getWebClient } from '@/services/ApolloClient'
import { GET_FEATURED_PROJECT_BY_ID_QUERY } from '@/services/CmsService/FeaturedProject'
import { useUserRegion } from '@/services/UserRegionService'
import { FeaturedProject, GetFeaturedProjectByIdQuery } from '@/types/codegen-contentful'
import { GetContentByProjectSlugQuery } from '@/types/codegen-federation'
import { useLocale } from '@/utils/LocaleUtil'
import { logger } from '@/utils/logging'

const GET_CONTENT_BY_PROJECT_SLUG = gql`
  query getContentByProjectSlug($slug: String!) {
    project(slug: $slug) {
      id
      title {
        __typename
        ... on Node {
          id
        }
        ... on ContentDisplayable {
          id
          description(version: SHORT)
        }
      }
    }
  }
`

interface UseFeaturedProjectOptions {
  featuredProjectId: string
  preview?: boolean
}

export type FederatedContent = NonNullable<NonNullable<GetContentByProjectSlugQuery['project']>['title']>

interface UseFeaturedProjectResult {
  featuredProject: FeaturedProject | undefined
  federatedContent: FederatedContent | undefined
  loading: boolean
  error: ErrorLike | undefined
  called: boolean
  refetch: () => void
  fetchFeaturedProject: () => void
}

export const useFeaturedProject = ({
  featuredProjectId,
  preview = false,
}: UseFeaturedProjectOptions): UseFeaturedProjectResult => {
  const { locale } = useLocale()
  const { region } = useUserRegion()

  const contentfulClient = getContentfulClient({ locale, preview })
  const federationClient = getWebClient({ locale })

  const [getFeaturedProjectById, { data, loading, error, called }] = useLazyQuery<GetFeaturedProjectByIdQuery>(
    GET_FEATURED_PROJECT_BY_ID_QUERY,
    {
      client: contentfulClient,
      errorPolicy: 'all',
    },
  )

  useEffect(() => {
    if (error) {
      logger().error(
        'useFeaturedProject query failed',
        {
          featuredProjectId,
          locale,
          region,
          preview,
          errorMessage: error.message,
          errorName: error.name,
          ...(CombinedGraphQLErrors.is(error)
            ? {
                graphQLErrors: error.errors.map((e) => ({
                  message: e.message,
                  path: e.path,
                  extensions: e.extensions,
                })),
              }
            : {}),
        },
        error,
      )
    }
  }, [error, featuredProjectId, locale, region, preview])

  useEffect(() => {
    if (data) {
      const items = data?.featuredProjectCollection?.items
      if (!Array.isArray(items) || items.length === 0 || !items[0]) {
        logger().warn('useFeaturedProject returned no items', {
          data,
          featuredProjectId,
          locale,
          region,
          preview,
          itemsLength: items?.length,
        })
      }
    }
  }, [data, featuredProjectId, locale, region, preview])

  const [getFederatedContent, { data: federatedData, loading: federatedLoading, error: federatedError }] =
    useLazyQuery<GetContentByProjectSlugQuery>(GET_CONTENT_BY_PROJECT_SLUG, {
      client: federationClient,
      errorPolicy: 'all',
    })

  useEffect(() => {
    if (federatedError) {
      logger().error('useFeaturedProject federation query failed', {
        projectSlug: data?.featuredProjectCollection?.items?.[0]?.projectSlug,
        errorMessage: federatedError.message,
        errorName: federatedError.name,
      })
    }
  }, [federatedError, data?.featuredProjectCollection?.items])

  const fetchFeaturedProject = () => {
    try {
      void getFeaturedProjectById({ variables: { id: featuredProjectId, region, locale, preview } })
    } catch (error) {
      logger().error(
        'useFeaturedProject query invocation error',
        {
          featuredProjectId,
          locale,
          region,
          preview,
        },
        error,
      )
    }
  }

  // Fetch federated content when we have the projectSlug
  useEffect(() => {
    const projectSlug = data?.featuredProjectCollection?.items?.[0]?.projectSlug
    if (projectSlug) {
      void getFederatedContent({ variables: { slug: projectSlug } })
    }
  }, [data, getFederatedContent])

  const featuredProject = data?.featuredProjectCollection?.items?.find(Boolean) as FeaturedProject | undefined

  const federatedContent = federatedData?.project?.title ?? undefined

  return {
    featuredProject,
    federatedContent,
    loading: loading || federatedLoading,
    error: error || federatedError,
    called,
    refetch: () => {
      void getFeaturedProjectById({ variables: { id: featuredProjectId, region, locale, preview } })
    },
    fetchFeaturedProject,
  }
}
